import { useState } from "react";
import {
    Box, Container, RadioGroup,
    FormControlLabel, Radio, useTheme,
    Divider,
    useMediaQuery
} from "@mui/material";
import {
    Send as SendIcon,
    RestartAlt as RestartIcon
} from "@mui/icons-material";

import {
    FilledButton, InitiaLogo, Textfield, Typography,
    useSnackbar, BasicLoader
} from "@initia-digital/component-library";
import { prodBase } from '@initia-digital/colors'
import {
    STUDY_AREAS, STUDY_AREAS_DESCRIPTIONS,
    STUDY_PROGRESS, STUDY_PROGRESS_DESCRIPTIONS
} from "@initia-digital/data-service-mapping";

import { BasicPublicAppBarPage } from "../layouts/BasicPublicAppBarPage";
import { useStore } from "../store";
import { postContact } from "../api/mutate";
import { CONFIG } from "../config/const";


export const Index = () => {
    // --------------------------------------------------------------
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState<{
        name?: string;
        email?: string;
        studyArea?: string;
        studyProgress?: string;
    }>({});

    const {
        editContactState, updateEditContactState,
        resetEditContactState
    } = useStore();

    const theme = useTheme();
    const isSmallerThanMd = useMediaQuery(theme.breakpoints.down('md'));

    const { queueSnackbar } = useSnackbar();

    // ---------------------
    const validateForm = () => {
        const newErrors: typeof errors = {};

        if (!editContactState.name?.trim()) {
            newErrors.name = 'Name is required';
        }

        if (!editContactState.email?.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^[\w.-]+@[\w.-]+\.[A-Za-z]+$/.test(editContactState.email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        if (!editContactState.studyArea) {
            newErrors.studyArea = 'Please select a study area';
        }

        if (!editContactState.studyProgress) {
            newErrors.studyProgress = 'Please select your study progress';
        }

        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) {
            window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
        }
        return Object.keys(newErrors).length === 0;
    };

    // ---------------------
    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);
        try {
            await postContact({
                ...editContactState,
                event: CONFIG.CURRENT_EVENT
            });
            queueSnackbar(
                `We've successfully received your details. Thanks for connecting with us.`,
                'success',
                `Thanks`,
                5000,
                '0.9rem'
            );
            resetEditContactState();
            setErrors({});
        } catch (error) {
            queueSnackbar(
                `We've failed to receive your details. You can try our backup form at: https://forms.gle/Ym5F4wXf9za6scrg9`,
                'error',
                `Sorry`,
                5000,
                '0.9rem'
            );
        } finally {
            setIsSubmitting(false);
        }
    };
    // ---------------------

    const handleFieldChange = (field: string, value: string) => {
        updateEditContactState({
            ...editContactState,
            [field]: value
        });
        setErrors({});
    };

    return (
        <BasicPublicAppBarPage>
            <Container maxWidth="sm">
                <Box display="flex" flexDirection="column">
                    <Box
                        display="flex" flexDirection="row"
                        alignItems="center" justifyContent="center"
                    >
                        <InitiaLogo width="100px" />
                    </Box>
                    <Box display="flex" flexDirection="column" sx={{ pb: Object.keys(errors).length > 0 ? '0px' : '24px' }}>
                        <Box
                            display="flex" flexDirection="row"
                            alignItems="center" justifyContent="flex-start"
                            mt="20px" mb="4px"
                        >
                            <Typography
                                fontSize="1.2rem"
                                fontWeight={700}
                                color={prodBase.primary.contrastText}
                            >
                                We're interested to hear from you
                            </Typography>
                        </Box>
                        <Box
                            display="flex" flexDirection="row"
                            alignItems="center" justifyContent="flex-start"
                            my="4px"
                        >
                            <Typography
                                fontSize="0.9rem"
                                fontWeight={300}
                                color={prodBase.primary.contrastText}
                            >
                                If you're open to hearing from us, please enter your details below
                            </Typography>
                        </Box>
                        <Box
                            display="flex" flexDirection="row"
                            alignItems="center" justifyContent="flex-start"
                            my="4px"
                        >
                            <Typography
                                fontSize="0.7rem"
                                fontWeight={300}
                                color={prodBase.secondary.main}
                            >
                                We'll keep your info for 90 days and only use it to send career opportunities.
                            </Typography>
                        </Box>
                        <Box
                            display="flex" flexDirection="column"
                            mt="16px" mb="4px"
                        >
                            <Textfield
                                value={editContactState.name}
                                name="name"
                                label="Name"
                                minRows={1}
                                placeholder="Name"
                                bgColor={prodBase.primary.main}
                                fontSize="0.9rem"
                                error={!!errors.name}
                                helperText={errors.name ?? '* Name is required'}
                                handleChange={(event) => {
                                    handleFieldChange('name', event.target.value);
                                }}
                            />
                        </Box>
                        <Box
                            display="flex" flexDirection="column"
                            my="4px"
                        >
                            <Textfield
                                value={editContactState.email}
                                name="email"
                                label="Email"
                                minRows={1}
                                placeholder="Email"
                                bgColor={prodBase.primary.main}
                                fontSize="0.9rem"
                                error={!!errors.email}
                                helperText={errors.email ?? '* Email is required'}
                                handleChange={(event) => {
                                    handleFieldChange('email', event.target.value);
                                }}
                            />
                        </Box>
                        <Box
                            display="flex" flexDirection="column"
                            my="4px"
                        >
                            <Textfield
                                value={editContactState.phone}
                                name="phone"
                                label="Mobile (optional)"
                                minRows={1}
                                placeholder="Mobile"
                                bgColor={prodBase.primary.main}
                                fontSize="0.9rem"
                                handleChange={(event) => {
                                    handleFieldChange('phone', event.target.value);
                                }}
                            />
                        </Box>
                        <Box
                            display="flex" flexDirection="column"
                            my="12px"
                        >
                            <Box
                                display="flex" flexDirection="row"
                                alignItems="center" justifyContent="flex-start"
                            >
                                <Box
                                    mr="10px"
                                >
                                    <Typography
                                        fontSize="0.9rem"
                                        fontWeight={500}
                                        color={errors.studyArea ? prodBase.primary.error : prodBase.primary.contrastText}
                                    >
                                        What is your study area?
                                    </Typography>
                                </Box>
                                <Typography
                                    fontSize="0.6rem"
                                    fontWeight={300}
                                    color={prodBase.primary.error}
                                >
                                    * Required
                                </Typography>
                            </Box>
                            <RadioGroup
                                value={editContactState.studyArea}
                                onChange={(event) => {
                                    handleFieldChange('studyArea', event.target.value);
                                }}
                                color={theme.palette.secondary.main}
                                sx={{
                                    px: "10px"
                                }}
                            >
                                {
                                    Object.values(STUDY_AREAS).map((studyArea) => (
                                        <FormControlLabel
                                            key={studyArea}
                                            value={studyArea}
                                            control={<Radio size="small" color="secondary" />}
                                            label={STUDY_AREAS_DESCRIPTIONS[studyArea]}
                                            color="secondary"
                                            sx={{
                                                '& .MuiFormControlLabel-label': {
                                                    fontSize: '0.9rem'
                                                }
                                            }}
                                        />
                                    ))
                                }
                            </RadioGroup>
                            {editContactState.studyArea === STUDY_AREAS.OTHER && (
                                <Box mt={1}>
                                    <Textfield
                                        value={editContactState.otherStudyAreaDescription}
                                        name="otherStudyAreaDescription"
                                        label="Please describe your study area"
                                        minRows={3}
                                        placeholder="Study area"
                                        bgColor={prodBase.primary.main}
                                        handleChange={(event) => {
                                            handleFieldChange('otherStudyAreaDescription', event.target.value);
                                        }}
                                    />
                                </Box>
                            )}
                        </Box>
                        <Divider />
                        <Box
                            display="flex" flexDirection="column"
                            my="12px"
                        >
                            <Box
                                display="flex" flexDirection="row"
                                alignItems="center" justifyContent="flex-start"
                            >
                                <Box
                                    mr="10px"
                                >
                                    <Typography
                                        fontSize="0.9rem"
                                        fontWeight={500}
                                        color={errors.studyProgress ? prodBase.primary.error : prodBase.primary.contrastText}
                                    >
                                        How far through your studies are you?
                                    </Typography>
                                </Box>
                                <Typography
                                    fontSize="0.6rem"
                                    fontWeight={300}
                                    color={prodBase.primary.error}
                                >
                                    * Required
                                </Typography>
                            </Box>
                            <RadioGroup
                                value={editContactState.studyProgress}
                                onChange={(event) => {
                                    handleFieldChange('studyProgress', event.target.value);
                                }}
                                color={theme.palette.secondary.main}
                                sx={{
                                    px: "10px"
                                }}
                            >
                                {
                                    Object.values(STUDY_PROGRESS).map((studyProgress) => (
                                        <FormControlLabel
                                            key={studyProgress}
                                            value={studyProgress}
                                            control={<Radio size="small" color="secondary" />}
                                            label={STUDY_PROGRESS_DESCRIPTIONS[studyProgress]}
                                            color="secondary"
                                            sx={{
                                                '& .MuiFormControlLabel-label': {
                                                    fontSize: '0.9rem'
                                                }
                                            }}
                                        />
                                    ))
                                }
                            </RadioGroup>
                            {editContactState.studyProgress === STUDY_PROGRESS.OTHER && (
                                <Box mt={1}>
                                    <Textfield
                                        value={editContactState.otherStudyProgressDescription}
                                        name="otherStudyProgressDescription"
                                        label="Please describe how far through your studies you are"
                                        minRows={3}
                                        placeholder="Describe your study progress"
                                        bgColor={prodBase.primary.main}
                                        handleChange={(event) => {
                                            handleFieldChange('otherStudyProgressDescription', event.target.value);
                                        }}
                                    />
                                </Box>
                            )}
                        </Box>
                        <Box
                            display="flex" flexDirection="column"
                            my="4px"
                        >
                            <Textfield
                                value={editContactState.comments}
                                name="comments"
                                label="Comments"
                                minRows={1}
                                placeholder="Any other comments"
                                bgColor={prodBase.primary.main}
                                fontSize="0.9rem"
                                handleChange={(event) => {
                                    handleFieldChange('comments', event.target.value);
                                }}
                            />
                        </Box>
                        <Box
                            display="flex" flexDirection="row"
                            alignItems="center" justifyContent="space-between"
                            mt="16px"
                            mb="16px"
                        >
                            <FilledButton
                                backgroundColor={'#0072b1'}
                                color={'white'}
                                disabled={isSubmitting}
                                size={isSmallerThanMd ? "small" : "medium"}
                                onClick={handleSubmit}
                                startIcon={isSubmitting ? <BasicLoader size={20} /> : <SendIcon />}
                            >
                                {isSubmitting ? 'Submitting...' : 'Submit'}
                            </FilledButton>
                            <FilledButton
                                backgroundColor={'#0072b1'}
                                color={'white'}
                                disabled={isSubmitting}
                                size={isSmallerThanMd ? "small" : "medium"}
                                onClick={() => {
                                    resetEditContactState();
                                    setErrors({});
                                }}
                                startIcon={<RestartIcon />}
                            >
                                Clear form
                            </FilledButton>
                        </Box>
                    </Box>                    
                    {Object.keys(errors).length > 0 && (
                            <Box
                                bgcolor="white"
                            >
                                <Container maxWidth="sm">
                                    <Typography
                                        fontSize="0.8rem"
                                        fontWeight={500}
                                        color={prodBase.primary.error}
                                    >
                                        Please check {Object.entries(errors)
                                            .map(([field,]) => {
                                                const fieldName = field === 'studyArea' ? 'study area' :
                                                    field === 'studyProgress' ? 'study progress' :
                                                        field;
                                                return fieldName;
                                            })
                                            .join(', ')}
                                    </Typography>
                                </Container>
                            </Box>
                        )}
                </Box>
            </Container>
        </BasicPublicAppBarPage>
    )
}